import React, { useMemo, useState } from 'react';
import { ACAO, ORIGEM, ORDEM_ORIGENS, Faixa90, Gravidade, diaIso, horasCpu, quandoTexto } from './comum';
import { BarraSegmentada, BarrasDia, GradeDiaHora, celulasDe } from './graficos';

// Resumo: "o que esta errado e quem resolve", em 30 segundos.
// Manchete -> 3 numeros -> quem usa a CPU -> acoes por dono -> quando.

function LinhaAcao({ p, max, onAbrir, onHover, ativo }) {
  return (
    <button type="button" className={`ab-acao ${ativo ? 'ab-acao--ativa' : ''}`} onClick={() => onAbrir(p)}
      onMouseEnter={() => onHover(p)} onMouseLeave={() => onHover(null)} onFocus={() => onHover(p)}>
      <Gravidade g={p.gravidade} so />
      <span className="ab-acao__titulo">
        <span className="ab-acao__nome">{p.tituloCurto || p.titulo}</span>
        <small className="cell-muted">{p.tipo === 'deadlock' ? 'deadlocks' : p.tipo === 'config' ? 'configuração' : p.tipo === 'cpu' ? 'consome CPU' : 'fica muito lenta às vezes'}{p.tipo === 'deadlock' && p.dono === 'misto' ? ' · disputa com o ERP' : ''}</small>
      </span>
      <span className="ab-acao__numero">{p.numeroTexto}<small>{p.unidade}</small></span>
      <span className="ab-acao__faixa">{p.porDia ? <Faixa90 porDia={p.porDia} max={max} cor={ORIGEM[p.dono]?.cor} /> : null}</span>
      <i className="bi bi-chevron-right ab-acao__seta" />
    </button>
  );
}

function ColunaAcoes({ acao, itens, max, onAbrir, onHover, ativo, onDossie, onVerTodos }) {
  const a = ACAO[acao];
  return (
    <section className="ab-coluna">
      <header className="ab-coluna__topo">
        <h3><i className={`bi ${a.icone}`} /> {a.titulo} <span className="ab-coluna__n">{itens.length}</span></h3>
        <button type="button" className="btn-portal btn-portal--outline" onClick={onDossie}>
          <i className="bi bi-file-earmark-text" /> {acao === 'micro' ? 'Dossiê para a Micro' : 'Lista do nosso lado'}
        </button>
      </header>
      {itens.length === 0 && <p className="cell-muted">Nada pendente.</p>}
      {itens.slice(0, 6).map((p) => <LinhaAcao key={p.id} p={p} max={max} onAbrir={onAbrir} onHover={onHover} ativo={ativo?.id === p.id} />)}
      {itens.length > 6 && <button type="button" className="ab-link" onClick={onVerTodos}>ver todos os {itens.length} <i className="bi bi-arrow-right" /></button>}
    </section>
  );
}

export default function Resumo({ dados, problemas, onAbrirProblema, onVerConsultas, onVerDia, onAba, onRelatorio }) {
  const [emFoco, setEmFoco] = useState(null);
  const itens = problemas?.itens || [];
  // Sistema nosso envolvido = correcao nossa: nada nosso vai para a Micro.
  const daMicro = itens.filter((p) => p.acao === 'micro');
  const nossos = itens.filter((p) => p.acao === 'nos');
  const maxDia = Math.max(1, ...itens.flatMap((p) => Object.values(p.porDia || {})));
  const totalDia = useMemo(() => (dados.deadlocksDia || []).reduce((acc, l) => {
    const k = diaIso(l.dia);
    acc[k] = (acc[k] || 0) + l.n;
    return acc;
  }, {}), [dados.deadlocksDia]);
  const focoDeadlock = emFoco?.tipo === 'deadlock' ? emFoco : null;
  const porOrigem = Object.fromEntries((dados.origens || []).map((o) => [o.origem, o]));
  const partes = ORDEM_ORIGENS.filter((o) => porOrigem[o]).map((o) => ({
    chave: o, rotulo: ORIGEM[o].rotulo, cor: ORIGEM[o].cor, valor: Number(porOrigem[o].cpu_ms), detalhe: `${horasCpu(porOrigem[o].cpu_ms)} de CPU`,
  }));

  return (
    <>
      <section className="ab-manchete">
        <p>{problemas?.manchete}</p>
        <button type="button" className="btn-portal btn-portal--outline" onClick={() => onRelatorio('diretoria')}>
          <i className="bi bi-printer" /> Versão para a diretoria
        </button>
      </section>

      <section className="ab-kpis">
        {(problemas?.kpis || []).map((k) => (
          <div key={k.rotulo} className="ab-kpi">
            <strong>{k.valor}</strong>
            <span>{k.rotulo}</span>
            <small className="cell-muted">{k.detalhe}</small>
          </div>
        ))}
      </section>

      <section className="content-card ab-bloco">
        <div className="ab-bloco__topo">
          <h3>Quem usa a CPU do servidor</h3>
          <span className="cell-muted">desde que o servidor ligou · clique para ver as consultas de cada um</span>
        </div>
        <BarraSegmentada partes={partes} onClick={onVerConsultas} />
      </section>

      <div className="ab-colunas">
        <ColunaAcoes acao="micro" itens={daMicro} max={maxDia} onAbrir={onAbrirProblema} onHover={setEmFoco} ativo={emFoco}
          onDossie={() => onRelatorio('micro')} onVerTodos={() => onAba('problemas', { acao: 'micro' })} />
        <ColunaAcoes acao="nos" itens={nossos} max={maxDia} onAbrir={onAbrirProblema} onHover={setEmFoco} ativo={emFoco}
          onDossie={() => onRelatorio('nos')} onVerTodos={() => onAba('problemas', { acao: 'nos' })} />
      </div>

      <div className="row g-3">
        <div className="col-12 col-xl-7">
          <section className="content-card ab-bloco">
            <div className="ab-bloco__topo">
              <h3>Quando os deadlocks acontecem</h3>
              <span className="cell-muted">
                {focoDeadlock ? `destaque: ${focoDeadlock.tituloCurto} · ${quandoTexto(focoDeadlock.quando, { curto: true })}` : 'últimos 90 dias · passe o mouse num problema ao lado para destacar · clique num dia'}
              </span>
            </div>
            <BarrasDia total={totalDia} destaque={focoDeadlock?.porDia} onDia={onVerDia}
              corDestaque={focoDeadlock ? ORIGEM[focoDeadlock.dono]?.cor : 'var(--ab-erp)'} />
          </section>
        </div>
        <div className="col-12 col-xl-5">
          <section className="content-card ab-bloco">
            <div className="ab-bloco__topo">
              <h3>Dia da semana × hora</h3>
              <span className="cell-muted">{focoDeadlock ? focoDeadlock.tituloCurto : 'todos os deadlocks, 90 dias'}</span>
            </div>
            <GradeDiaHora celulas={focoDeadlock?.porHora || celulasDe(dados.deadlocksGrade)} />
          </section>
        </div>
      </div>

      <p className="ab-rodape cell-muted">
        Dados copiados do próprio SQL Server (só leitura). CPU: acumulado desde que o servidor ligou. Deadlocks: 90 dias, com data e hora.
        O horário das consultas do ERP passa a existir com a coleta a cada 15 minutos.
      </p>
    </>
  );
}
